export const DAY_NAMES = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miercoles',
  'Jueves',
  'Viernes',
  'Sabado'
]

export const asArray = (value) => {
  if (Array.isArray(value)) return value
  if (Array.isArray(value?.items)) return value.items
  if (Array.isArray(value?.data)) return value.data
  if (Array.isArray(value?.$values)) return value.$values
  return []
}

export const normalizeRule = (rule) => ({
  dayOfWeek: Number(rule?.dayOfWeek ?? rule?.DayOfWeek ?? 0),
  startTime: String(rule?.startTime ?? rule?.StartTime ?? '08:00').slice(0, 5),
  endTime: String(rule?.endTime ?? rule?.EndTime ?? '17:00').slice(0, 5),
  isActive: Boolean(rule?.isActive ?? rule?.IsActive ?? true)
})

export const normalizeUtcString = (value) => {
  const raw = String(value || '').trim()
  if (!raw) return ''
  return /([zZ]|[+-]\d{2}:\d{2})$/.test(raw) ? raw : `${raw}Z`
}

export const parseUtcDate = (value) => new Date(normalizeUtcString(value))

export const normalizeAppointment = (appointment) => ({
  id: appointment?.id ?? appointment?.Id,
  startAtUtc: normalizeUtcString(appointment?.startAtUtc ?? appointment?.StartAtUtc),
  endAtUtc: normalizeUtcString(appointment?.endAtUtc ?? appointment?.EndAtUtc),
  status: String(appointment?.status ?? appointment?.Status ?? 'scheduled').toLowerCase(),
  notes: appointment?.notes ?? appointment?.Notes ?? '',
  serviceName: appointment?.service?.name ?? appointment?.service?.Name ?? appointment?.Service?.Name ?? 'Servicio',
  clientName: appointment?.client?.fullName ?? appointment?.client?.FullName ?? appointment?.Client?.FullName ?? 'Cliente',
  clientEmail: appointment?.client?.email ?? appointment?.client?.Email ?? appointment?.Client?.Email ?? ''
})

export const getDateKey = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const parseTimeToMinutes = (value) => {
  const [hours, minutes] = String(value || '00:00').split(':').map(Number)
  return (hours * 60) + minutes
}

export const formatMinutesLabel = (minutes) => {
  const safe = Math.max(0, Number(minutes || 0))
  const hours = Math.floor(safe / 60)
  const mins = safe % 60
  if (hours === 0) return `${mins} min`
  if (mins === 0) return `${hours} h`
  return `${hours} h ${mins} min`
}

export const formatTimeLabel = (value) =>
  new Intl.DateTimeFormat('es-CO', {
    hour: 'numeric',
    minute: '2-digit'
  }).format(value instanceof Date ? value : parseUtcDate(value))

export const buildMonthGrid = (referenceDate) => {
  const year = referenceDate.getFullYear()
  const month = referenceDate.getMonth()
  const firstDay = new Date(year, month, 1)
  const firstWeekday = firstDay.getDay()
  const gridStart = new Date(year, month, 1 - firstWeekday)

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart)
    date.setDate(gridStart.getDate() + index)
    return date
  })
}

export const buildDayBlocks = ({ date, rules, appointments }) => {
  const weekday = date.getDay()
  const dayRules = (rules || []).filter((rule) => rule.isActive && rule.dayOfWeek === weekday)
  const dayStart = new Date(date)
  dayStart.setHours(0, 0, 0, 0)
  const dayEnd = new Date(date)
  dayEnd.setHours(23, 59, 59, 999)

  const dayAppointments = (appointments || [])
    .filter((appointment) => {
      const start = parseUtcDate(appointment.startAtUtc)
      const end = parseUtcDate(appointment.endAtUtc)
      return end > dayStart && start < dayEnd && appointment.status !== 'cancelled'
    })
    .sort((left, right) => parseUtcDate(left.startAtUtc) - parseUtcDate(right.startAtUtc))

  const blocks = []

  dayRules.forEach((rule, ruleIndex) => {
    const startMinutes = parseTimeToMinutes(rule.startTime)
    const endMinutes = parseTimeToMinutes(rule.endTime)
    const ruleStart = new Date(date)
    ruleStart.setHours(Math.floor(startMinutes / 60), startMinutes % 60, 0, 0)
    const ruleEnd = new Date(date)
    ruleEnd.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0)

    const overlappingAppointments = dayAppointments
      .map((appointment) => ({
        ...appointment,
        localStart: parseUtcDate(appointment.startAtUtc),
        localEnd: parseUtcDate(appointment.endAtUtc)
      }))
      .filter((appointment) => appointment.localEnd > ruleStart && appointment.localStart < ruleEnd)
      .sort((left, right) => left.localStart - right.localStart)

    let cursor = new Date(ruleStart)

    overlappingAppointments.forEach((appointment) => {
      const appointmentStart = appointment.localStart < ruleStart ? ruleStart : appointment.localStart
      const appointmentEnd = appointment.localEnd > ruleEnd ? ruleEnd : appointment.localEnd

      if (appointmentStart > cursor) {
        blocks.push({
          id: `available-${ruleIndex}-${cursor.toISOString()}`,
          type: 'available',
          start: new Date(cursor),
          end: new Date(appointmentStart)
        })
      }

      blocks.push({
        id: `scheduled-${appointment.id}`,
        type: 'scheduled',
        start: new Date(appointmentStart),
        end: new Date(appointmentEnd),
        appointment
      })

      if (appointmentEnd > cursor) {
        cursor = new Date(appointmentEnd)
      }
    })

    if (cursor < ruleEnd) {
      blocks.push({
        id: `available-${ruleIndex}-${cursor.toISOString()}-end`,
        type: 'available',
        start: new Date(cursor),
        end: new Date(ruleEnd)
      })
    }
  })

  return blocks.sort((left, right) => left.start - right.start)
}

export const summarizeDay = ({ date, rules, appointments }) => {
  const blocks = buildDayBlocks({ date, rules, appointments })
  const scheduledBlocks = blocks.filter((block) => block.type === 'scheduled')
  const availableBlocks = blocks.filter((block) => block.type === 'available')
  const availableMinutes = availableBlocks.reduce((sum, block) => sum + ((block.end - block.start) / 60000), 0)
  const scheduledMinutes = scheduledBlocks.reduce((sum, block) => sum + ((block.end - block.start) / 60000), 0)

  return {
    blocks,
    scheduledCount: scheduledBlocks.length,
    availableCount: availableBlocks.length,
    availableMinutes,
    scheduledMinutes,
    hasAvailability: availableBlocks.length > 0,
    hasAppointments: scheduledBlocks.length > 0
  }
}
